import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';
import XHR from 'i18next-xhr-backend'; // have a own xhr fallback
import _ from 'lodash';
import Configuration from '../dispatcher/config/Configuration';

// <debug>
Object.assign(process.env, {
  // set process.env for TEST environment
  NODE_ENV: process.env.NODE_ENV,
  i18n: {
    logMissingTranslation: true,
    separateTranslationPerNamespaces: true
  }
});
// </debug>
const { I18N_HASH, BASE_ROUTE_NAME: APP_SUBDIRECTORY } = Configuration.get();
let BACKEND_LOAD_PATH = _.get(process, 'env.i18n.separateTranslationPerNamespaces') ? '/locales/{{ns}}/{{lng}}.json' : '/locales/{{lng}}.json';
BACKEND_LOAD_PATH += `?dc=${I18N_HASH}`;
if (APP_SUBDIRECTORY.length) {
  BACKEND_LOAD_PATH = APP_SUBDIRECTORY + BACKEND_LOAD_PATH;
}

// <debug>
const resources = require('./Locales.js').default;
// </debug>

const DEV_MODE = process.env.NODE_ENV === 'development';
const defaultLocale = 'en-US';

const stylesAppliedToLocalisation = ['font-weight', 'color']; // styles applied in order
const styleAppliedSeparator = ':';

/**
 * Format text based on style.
 * @param text Localised text value.
 * @param styleValue Values to pre format passed text.
 * @returns {string}
 */
const formatText = (text, styleValue = '') => {
  const styleValues = styleValue.split(styleAppliedSeparator);
  text = stylesAppliedToLocalisation.reduce((value, style, index) => {
    if (value.match(style)) {
      value = value.replace(`${style}:inherit`, `${style}:${styleValues[index] || 'inherit'}`);
    }
    return value;
  }, text);

  return text;
};

let namespaces = ['translation', 'common'];
// <debug>
if (DEV_MODE) {
  namespaces.push('storybook');
}
// </debug>

let options = {
  ns: namespaces,
  cleanCode: true, // language will be lowercased EN --> en while leaving full locales like en-US
  lng: defaultLocale,
  fallbackLng: defaultLocale,
  keySeparator: '.', // don't use keys in form `message.welcome`
  returnObjects: true,
  interpolation: {
    nestingPrefix: '[[',
    nestingSuffix: ']]',
    escapeValue: false, // react already safes from xss
    format: function (value, format, lng) {
      // to uppercase
      if (format === 'uppercase') return value.toUpperCase();

      // to date format
      if (value instanceof Date) {
        return dayjs(value).format(format);
      }

      // to font style format {{sFontFormat, bolder:#50ef14}} ... some text ... {{eFontStyle}}
      const formatted = formatText(value, format);
      if (formatted) {
        return formatted;
      }

      return value;
    }
  },
  react: {
    useSuspense: false,
    transSupportBasicHtmlNodes: true,
    transKeepBasicHtmlNodesFor: ['br', 'strong', 'i'],
    wait: true,
    bindI18n: 'initialized languageChanged loaded',
    bindI18nStore: 'initialized languageChanged loaded'
  },
  load: defaultLocale,
  partialBundledLanguages: false, // allows some resources to be set on initialization while others can be loaded using a backend connector
  preload: [defaultLocale]
};

// <debug>
if (DEV_MODE) {
  resources[defaultLocale] = {
    common: resources.common,
    translation: resources.translation,
    storybook: resources.storybook
  };
  resources['de-DE'] = {
    common: resources.common,
    translation: resources.translation,
    storybook: {
      ...resources.storybook,
      Cookie: {
        activeCookie: 'Aktive Cookies'
      },
      ChangeLanguage: {
        currentLanguage: 'Aktuelle Sprache'
      }
    }
  };
}
// </debug>

Object.assign(
  options,
  DEV_MODE
    ? {
      // <debug>
      resources,
      debug: _.get(process, 'env.i18n.logMissingTranslation') // Enable logging of missing translation
      // </debug>
    }
    : {
      backend: {
        loadPath: BACKEND_LOAD_PATH,
        allowMultiLoading: true // required by backend loader to load multiple files on demand
      }
    }
);

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(DEV_MODE ? {} : XHR)
  .init(options);

// <debug>
window.i18n = i18n;
// </debug>

export default i18n;
